/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: promptGenerator.ts
 🗓️ Created: 2026-01-24
 🧩 Description:
    Utility for generating AI Agent system prompts following
    OpenAI Realtime Prompting Guide (8-section format).
    Used by both PromptPreviewModal and auto-generation on save.
────────────────────────────────────────────────────────────────*/

export interface PromptFormData {
    persona_name?: string;
    role_description?: string;
    personality?: string;
    tone_of_voice?: string;
    language_level?: string;
    speaking_style?: string;
    response_length?: string;
    filler_words?: boolean;
    reduce_repetition?: boolean;
    allow_language_change?: boolean;
    forced_language?: string;
    company_name?: string;
    products?: string[];
    key_points?: string;
    do_rules?: string[];
    dont_rules?: string[];
    greeting_message?: string;
    closing_message?: string;
    disclosure_message?: string;
    additional_instructions?: string;
    team_members?: Record<string, string>;
    pronunciations?: Record<string, string>;
    enable_transfer?: boolean;
    enable_hangup?: boolean;
    transfer_routes?: { name: string; destination_type: string; destination: string }[];
}

// Map personality values to natural language descriptions
const getPersonalityDescription = (key: string | undefined): string => {
    const map: Record<string, string> = {
        'friendly': 'friendly and approachable',
        'professional': 'professional and polished',
        'calm': 'calm and patient',
        'enthusiastic': 'enthusiastic and energetic',
        'empathetic': 'empathetic and supportive'
    };
    return map[key || 'friendly'] || 'friendly and approachable';
};

const getToneDescription = (key: string | undefined): string => {
    const map: Record<string, string> = {
        'warm': 'warm',
        'confident': 'confident',
        'neutral': 'neutral',
        'encouraging': 'encouraging',
        'direct': 'direct'
    };
    return map[key || 'warm'] || 'warm';
};

const getLanguageLevelDescription = (key: string | undefined): string => {
    const map: Record<string, string> = {
        'casual': 'casual and informal',
        'conversational': 'conversational and clear',
        'formal': 'formal and professional',
        'technical': 'technical with domain-specific terminology'
    };
    return map[key || 'conversational'] || 'conversational and clear';
};

const getSpeakingStyleDescription = (key: string | undefined): string => {
    const map: Record<string, string> = {
        'natural': 'natural with balanced pacing',
        'fast_paced': 'fast-paced but clear',
        'slow_clear': 'slow and deliberate for clarity',
        'expressive': 'expressive with dynamic emotion'
    };
    return map[key || 'natural'] || 'natural with balanced pacing';
};

const getResponseLengthDescription = (key: string | undefined): string => {
    const map: Record<string, string> = {
        'brief': '1-2 sentences per turn',
        'standard': '2-3 sentences per turn',
        'detailed': '3-4 sentences per turn'
    };
    return map[key || 'standard'] || '2-3 sentences per turn';
};

/**
 * Generate a structured system prompt following OpenAI Realtime Prompting Guide
 * Target: 200-500 tokens for optimal real-time performance
 */
export function generatePromptFromFormData(formData: PromptFormData): string {
    const lines: string[] = [];

    // ═══════════════════════════════════════════════════════════════
    // SECTION 1: ROLE & OBJECTIVE (OpenAI recommended)
    // ═══════════════════════════════════════════════════════════════
    lines.push('# ROLE & OBJECTIVE\n');

    if (formData.persona_name) {
        lines.push(`You are ${formData.persona_name}.`);
    }
    if (formData.role_description) {
        lines.push(`${formData.role_description}`);
    }
    if (formData.company_name) {
        lines.push(`You represent ${formData.company_name}.`);
    }
    lines.push('');

    // ═══════════════════════════════════════════════════════════════
    // SECTION 2: PERSONALITY & TONE (OpenAI recommended)
    // ═══════════════════════════════════════════════════════════════
    lines.push('# PERSONALITY & TONE\n');
    lines.push(`- Be ${getPersonalityDescription(formData.personality)} in your interactions`);
    lines.push(`- Use a ${getToneDescription(formData.tone_of_voice)} tone`);
    lines.push(`- Keep language ${getLanguageLevelDescription(formData.language_level)}`);
    lines.push(`- Speak with a ${getSpeakingStyleDescription(formData.speaking_style)} delivery`);
    lines.push(`- Keep responses ${getResponseLengthDescription(formData.response_length)}`);

    if (formData.filler_words) {
        lines.push('- Use natural filler words like "um", "well", "you know" occasionally for authenticity');
    }
    if (formData.reduce_repetition) {
        lines.push('- Vary your phrasing to avoid sounding repetitive or robotic');
    }
    lines.push('');

    // ═══════════════════════════════════════════════════════════════
    // SECTION 3: CONTEXT (Products, Key Info, Team)
    // ═══════════════════════════════════════════════════════════════
    const hasContext = (formData.products && formData.products.length > 0) ||
        formData.key_points ||
        (formData.team_members && Object.keys(formData.team_members).length > 0);

    if (hasContext) {
        lines.push('# CONTEXT\n');

        if (formData.products && formData.products.length > 0) {
            lines.push('**Products/Services:**');
            formData.products.forEach(product => {
                lines.push(`- ${product}`);
            });
            lines.push('');
        }

        if (formData.key_points) {
            lines.push('**Key Information:**');
            lines.push(formData.key_points);
            lines.push('');
        }

        if (formData.team_members && Object.keys(formData.team_members).length > 0) {
            lines.push('**Team Members:**');
            Object.entries(formData.team_members).forEach(([name, role]) => {
                lines.push(`- ${name}: ${role}`);
            });
            lines.push('');
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // SECTION 4: REFERENCE PRONUNCIATIONS (OpenAI recommended)
    // ═══════════════════════════════════════════════════════════════
    if (formData.pronunciations && Object.keys(formData.pronunciations).length > 0) {
        lines.push('# REFERENCE PRONUNCIATIONS\n');
        Object.entries(formData.pronunciations).forEach(([word, pronunciation]) => {
            lines.push(`- "${word}" → say as "${pronunciation}"`);
        });
        lines.push('');
    }

    // ═══════════════════════════════════════════════════════════════
    // SECTION 5: TOOLS (Function calling capabilities)
    // ═══════════════════════════════════════════════════════════════
    const hasTools = formData.enable_transfer || formData.enable_hangup;

    if (hasTools) {
        lines.push('# TOOLS\n');
        lines.push('You have access to the following functions:');

        if (formData.enable_hangup) {
            lines.push('- **hangup**: End the call when appropriate (after saying goodbye)');
        }
        if (formData.enable_transfer) {
            lines.push('- **transfer_call**: Transfer to another destination');
            if (formData.transfer_routes && formData.transfer_routes.length > 0) {
                lines.push('\n**Transfer Routes:**');
                formData.transfer_routes.forEach(route => {
                    if (route.name && route.destination_type) {
                        lines.push(`- ${route.name}: Transfer to ${route.destination_type}`);
                    }
                });
            }
        }
        lines.push('');
    }

    // ═══════════════════════════════════════════════════════════════
    // SECTION 6: INSTRUCTIONS / RULES (DO and DON'T)
    // ═══════════════════════════════════════════════════════════════
    const hasRules = (formData.do_rules && formData.do_rules.length > 0) ||
        (formData.dont_rules && formData.dont_rules.length > 0) ||
        !formData.allow_language_change;

    if (hasRules) {
        lines.push('# INSTRUCTIONS\n');

        if (formData.do_rules && formData.do_rules.length > 0) {
            lines.push('**ALWAYS:**');
            formData.do_rules.forEach(rule => {
                lines.push(`- ${rule}`);
            });
            lines.push('');
        }

        if (formData.dont_rules && formData.dont_rules.length > 0) {
            lines.push('**NEVER:**');
            formData.dont_rules.forEach(rule => {
                lines.push(`- ${rule}`);
            });
            lines.push('');
        }

        // Language control rule
        if (!formData.allow_language_change && formData.forced_language) {
            lines.push('**CRITICAL LANGUAGE RULE:**');
            lines.push(`- ALWAYS respond in ${formData.forced_language}, regardless of what language the caller uses`);
            lines.push('');
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // SECTION 7: CONVERSATION FLOW
    // ═══════════════════════════════════════════════════════════════
    if (formData.greeting_message || formData.closing_message) {
        lines.push('# CONVERSATION FLOW\n');
        if (formData.greeting_message) {
            lines.push(`**Opening:** Start with: "${formData.greeting_message}". Adapt and translate this opening message naturally to the language in which the user initiates the conversation.`);
        }
        if (formData.closing_message) {
            lines.push(`**Closing:** End calls with: "${formData.closing_message}". Adapt and translate this closing message naturally to the language used during the conversation.`);
        }
        lines.push('');
    }

    // ═══════════════════════════════════════════════════════════════
    // SECTION 8: SAFETY & ESCALATION (AI Disclosure)
    // ═══════════════════════════════════════════════════════════════
    if (formData.disclosure_message) {
        lines.push('# SAFETY & ESCALATION\n');
        lines.push(`If asked whether you are an AI or human, respond honestly: "${formData.disclosure_message}"`);
        lines.push('');
    }

    // ═══════════════════════════════════════════════════════════════
    // ADDITIONAL INSTRUCTIONS (Custom user content)
    // ═══════════════════════════════════════════════════════════════
    if (formData.additional_instructions && formData.additional_instructions.trim()) {
        lines.push('# ADDITIONAL INSTRUCTIONS\n');
        lines.push(formData.additional_instructions);
        lines.push('');
    }

    return lines.join('\n').trim();
}

/**
 * Estimate token count (roughly 4 characters per token for English)
 */
export function estimateTokens(text: string): number {
    if (!text) return 0;
    return Math.ceil(text.length / 4);
}
